import type { Rep } from "@repo/models";
import type { GridList } from "@repo/ui";
import type { UseQueryResult } from "@tanstack/react-query";

import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import type { RepsFilter } from "../../api/repsApi";

import { QUERY_KEYS } from "../../api/queries";
import RepsApi from "../../api/repsApi";

/** Sorts the slim projection can order by: every name here must be mapped in RepresentativeMapper.SlimConfig. */
const SLIM_SUPPORTED_PROPERTIES = new Set([
	"repId",
	"id",
	"segmentId",
	"login",
	"agentId",
	"firstName",
	"middleName",
	"lastName",
	"dateOfBirth",
	"pin",
	"phone",
	"email",
	"statusName",
	"active",
	"brand",
	"channel",
	"vendor",
	"branchName",
	"state",
	"position",
	"updatedOn",
	"createdOn",
]);

export function useRepsData(filters: RepsFilter) {
	const isSlimSupported = useMemo(() => {
		const sortBy = filters.sortBy?.name || (filters as Record<string, any>)["sortBy.name"];
		if (!sortBy) return true;
		return SLIM_SUPPORTED_PROPERTIES.has(sortBy);
	}, [filters]);

	const repsSlimQuery = useQuery({
		queryKey: [QUERY_KEYS.Representatives.Reps, "slim", filters],
		queryFn: ({ signal }) => RepsApi.getReps({ ...filters, isSlim: true }, { signal }),
		enabled: isSlimSupported,
	});

	const repsFullQuery = useQuery({
		queryKey: [QUERY_KEYS.Representatives.Reps, "full", filters],
		queryFn: ({ signal }) => RepsApi.getReps({ ...filters, isSlim: false }, { signal }),
	});

	const mergedData = useMemo(() => {
		if (!isSlimSupported) return repsFullQuery.data;

		if (!repsSlimQuery.data?.items) return undefined;
		if (!repsFullQuery.data) {
			return {
				...repsSlimQuery.data,
				items: repsSlimQuery.data.items.map((row) => ({
					...row,
					id: row.segmentId ?? row.repId,
					branchName: row.branchName,
					startDate: row.createdOn,
					dateLastModified: row.updatedOn,
					login: row.login,
					agentId: row.agentId,
				})),
			};
		}

		const fullMap = new Map(repsFullQuery.data.items.map((r) => [r.segmentId ?? r.repId, r]));
		return {
			...repsSlimQuery.data,
			items: repsSlimQuery.data.items.map((row) => {
				const full = fullMap.get(row.segmentId ?? row.repId);
				return {
					...row,
					id: row.segmentId ?? row.repId,
					branchName: row.branchName,
					startDate: row.createdOn,
					dateLastModified: row.updatedOn,
					login: row.login,
					agentId: row.agentId,
					...full,
				};
			}),
		};
	}, [isSlimSupported, repsSlimQuery.data, repsFullQuery.data]);

	const repsQueryProxy = useMemo(() => {
		const activeQuery = isSlimSupported ? repsSlimQuery : repsFullQuery;
		return {
			...activeQuery,
			data: mergedData,
			isEnriching: isSlimSupported && repsFullQuery.isFetching,
		} as UseQueryResult<GridList<Rep>> & { isEnriching: boolean };
	}, [isSlimSupported, repsSlimQuery, repsFullQuery, mergedData]);

	const refetch = useCallback(async () => {
		await repsSlimQuery.refetch();
		await repsFullQuery.refetch();
	}, [repsSlimQuery, repsFullQuery]);

	return {
		repsQueryProxy,
		refetch,
	};
}
